import React, { useState } from 'react';
import { 
    IoClose, 
    IoCheckmarkCircle, 
    IoPersonOutline, 
    IoMailOutline, 
    IoCallOutline, 
    IoLocationOutline,
    IoRocketOutline
} from 'react-icons/io5';

export default function ContactModal({ isOpen, onClose }) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [countryCode, setCountryCode] = useState('+91');
    const [mobileNumber, setMobileNumber] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [city, setCity] = useState('');

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSuccess(true);
            setTimeout(() => {
                handleClose();
            }, 4000);
        }, 1200);
    };

    const handleClose = () => {
        setIsSuccess(false);
        setIsSubmitting(false);
        setMobileNumber('');
        setName('');
        setEmail('');
        setCity('');
        onClose();
    };

    const handleMobileChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        if (val.length <= 12) {
            setMobileNumber(val);
        }
    };

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6 sm:py-12 animate-fade-in">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-md transition-opacity"
                onClick={handleClose}
            />

            {/* Modal Card */}
            <div className="relative w-full max-w-[460px] bg-white rounded-3xl shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-slate-100 overflow-hidden transform transition-all animate-scale-up">
                
                {/* Decorative top gradient */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#73C8BD] via-[#5db8ad] to-[#73C8BD]" />

                {/* Close Button */}
                <button 
                    onClick={handleClose}
                    className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-800 transition-all focus:outline-none focus:ring-2 focus:ring-[#73C8BD]/50"
                    aria-label="Close modal"
                >
                    <IoClose className="text-xl" />
                </button>

                {isSuccess ? (
                    <div className="px-8 py-16 flex flex-col items-center justify-center text-center animate-fade-in">
                        <div className="relative mb-6">
                            <div className="absolute inset-0 bg-emerald-100 rounded-full animate-ping opacity-20"></div>
                            <div className="relative w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 shadow-sm border border-emerald-100">
                                <IoCheckmarkCircle className="text-5xl" />
                            </div>
                        </div>
                        <h3 className="text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
                            Request Received!
                        </h3>
                        <p className="text-slate-500 mb-8 leading-relaxed max-w-[300px] text-sm sm:text-base">
                            Thanks, <span className="font-semibold text-slate-700">{name || 'there'}</span>! Our franchise team is reviewing your details and will connect with you shortly.
                        </p>
                        <button
                            onClick={handleClose}
                            className="w-full sm:w-auto min-w-[200px] h-12 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-2"
                        >
                            Got it, thanks
                        </button>
                    </div>
                ) : (
                    <div className="px-7 sm:px-10 py-8 sm:py-10">
                        {/* Header */}
                        <div className="mb-8 pr-8">
                            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#EAF8F5] text-[#73C8BD] mb-4 shadow-sm border border-[#73C8BD]/20">
                                <IoRocketOutline className="text-2xl" />
                            </div>
                            <h3 className="text-2xl sm:text-[28px] leading-tight font-extrabold text-slate-900 tracking-tight mb-2.5">
                                Start Your Journey
                            </h3>
                            <p className="text-slate-500 text-sm leading-relaxed font-medium">
                                Get our detailed franchise prospectus, including setup costs and ROI breakdown.
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="space-y-4">
                            
                            {/* Full Name */}
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <IoPersonOutline className="text-slate-400 group-focus-within:text-[#73C8BD] transition-colors text-lg" />
                                </div>
                                <input 
                                    type="text" 
                                    required 
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Full Name *"
                                    className="w-full h-12 pl-11 pr-4 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-[#73C8BD] focus:ring-[3px] focus:ring-[#73C8BD]/15 transition-all font-medium text-sm"
                                />
                            </div>

                            {/* Email Address */}
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <IoMailOutline className="text-slate-400 group-focus-within:text-[#73C8BD] transition-colors text-lg" />
                                </div>
                                <input 
                                    type="email" 
                                    required 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Email Address *"
                                    className="w-full h-12 pl-11 pr-4 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-[#73C8BD] focus:ring-[3px] focus:ring-[#73C8BD]/15 transition-all font-medium text-sm"
                                />
                            </div>

                            {/* Mobile Number */}
                            <div className="flex gap-3">
                                <div className="relative w-[100px]">
                                    <select 
                                        value={countryCode}
                                        onChange={(e) => setCountryCode(e.target.value)}
                                        className="w-full h-12 pl-3 pr-6 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-700 font-semibold text-sm focus:bg-white focus:outline-none focus:border-[#73C8BD] focus:ring-[3px] focus:ring-[#73C8BD]/15 transition-all cursor-pointer appearance-none text-center"
                                        aria-label="Country Code"
                                    >
                                        <option value="+91">+91</option>
                                        <option value="+1">+1</option>
                                        <option value="+44">+44</option>
                                        <option value="+971">+971</option>
                                        <option value="+65">+65</option>
                                    </select>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                    </div>
                                </div>
                                <div className="relative group flex-1">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <IoCallOutline className="text-slate-400 group-focus-within:text-[#73C8BD] transition-colors text-lg" />
                                    </div>
                                    <input 
                                        type="tel" 
                                        required 
                                        value={mobileNumber}
                                        onChange={handleMobileChange}
                                        placeholder="Phone Number *"
                                        className="w-full h-12 pl-11 pr-4 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-[#73C8BD] focus:ring-[3px] focus:ring-[#73C8BD]/15 transition-all font-medium text-sm"
                                    />
                                </div>
                            </div>

                            {/* City / Location */}
                            <div className="relative group">
                                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                    <IoLocationOutline className="text-slate-400 group-focus-within:text-[#73C8BD] transition-colors text-lg" />
                                </div>
                                <input 
                                    type="text" 
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Preferred City / District"
                                    className="w-full h-12 pl-11 pr-4 bg-slate-50/70 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-[#73C8BD] focus:ring-[3px] focus:ring-[#73C8BD]/15 transition-all font-medium text-sm"
                                />
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4">
                                <button 
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="group relative w-full h-14 rounded-xl bg-slate-900 text-white font-semibold text-base overflow-hidden shadow-[0_8px_16px_-6px_rgba(0,0,0,0.3)] transition-all duration-300 hover:shadow-[0_12px_20px_-8px_rgba(0,0,0,0.4)] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:active:scale-100"
                                >
                                    {/* Hover gradient effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-[#73C8BD] to-[#55ad9f] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    
                                    <div className="relative flex items-center justify-center gap-2.5">
                                        {isSubmitting ? (
                                            <>
                                                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Request Franchise Kit</span>
                                                <IoRocketOutline className="text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
                                            </>
                                        )}
                                    </div>
                                </button>
                            </div>

                            {/* Reassurance Note */}
                            <div className="text-center pt-3">
                                <p className="text-[13px] text-slate-500 font-medium inline-flex items-center gap-2">
                                    <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                                    Your information is secure
                                </p>
                            </div>

                        </form>
                    </div>
                )}
            </div>
        </div>
    );
}
